import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '@column/column-ui-kit';
import { PopoverPlatform, PopoverPlatformType } from '~/components';
import { clientWrapper, PlatformRoleName, UserRepository, userGroups } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { ROUTE } from '~/app/routes';
import { log } from '~/util';
import { reCaptcha } from '~/util/reCaptcha';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  gap: 8px;
  line-height: 20px;
  font-size: 14px;
`;

const Label = styled.div`
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
`;

const Action = styled.div`
  position: relative;
  display: flex;
  gap: 2px;
`;

const ActionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  line-height: 20px;
  color: ${({ theme }) => theme.secondary.background};
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.foreground};
  }
`;

const PlatformName = styled.div``;

const Invites = styled.div`
  width: 16px;
  height: 16px;
  line-height: 16px;
  text-align: center;
  border-radius: 50%;
  font-size: 10px;
  font-weight: bold;
  color: ${({ theme }) => theme.primary.foreground};
  background-color: ${({ theme }) => theme.primary.background};
`;

const Arrow = styled.div`
  &:hover {
    & > svg {
      --icon-color: ${({ theme }) => theme.secondary.background};
    }
  }

  & > svg {
    --icon-size: 20px;
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};

    z-index: 10;
    position: relative;
    cursor: pointer;

    path {
      transition: stroke 0.2s, fill 0.2s;
    }
  }
`;

const StyledPopoverPlatform = styled(PopoverPlatform)`
  --popover-left: -20px;
  --popover-top: 26px;
  --popover-min-width: 320px;
`;

export const Platform: React.FC = () => {
  const { isLoading, currentUser, currentPlatform } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const [showPopoverPlatform, setShowPopoverPlatform] = useState<boolean>(false);
  const [platforms, setPlatforms] = useState<PopoverPlatformType[]>([]);
  const [invites, setInvites] = useState<PopoverPlatformType[]>([]);

  const acceptInvite = (id: string) => {
    clientWrapper(
      () => {
        addSuccessNotification({
          content: 'Invite accepted',
          display: 'page',
        });

        setShowPopoverPlatform(false);
        fetchData();
      },
      (e) =>
        addDangerNotification({
          content: e.message,
          display: 'page',
        }),
      UserRepository.acceptInvite(id)
    );
  };

  const activeChange = (id: string) => {
    if (!currentUser) {
      return;
    }
    reCaptcha(
      (reCaptchaToken) =>
        currentUser
          .update({ defaultPlatformId: id }, reCaptchaToken)
          .then(() => {
            log({
              name: 'Platform changed',
              context: currentUser,
            });
            addSuccessNotification({
              content: 'Platform changed',
              display: 'page',
            });
            setShowPopoverPlatform(false);
            fetchData();
          })
          .catch((error) => {
            addDangerNotification({
              content: error.message,
              display: 'page',
            });
          }),
      () => {
        addDangerNotification({
          content: 'Error',
          display: 'page',
        });
      }
    );
  };

  const manageClick = () => {
    setShowPopoverPlatform(false);
    navigate(ROUTE.PLATFORMS);
  };

  const fetchData = useCallback(() => {
    if (!currentUser?.id) {
      return;
    }

    clientWrapper(
      (response) => {
        const entries: PopoverPlatformType[] = [];

        if (response.platformRoles.length) {
          response.platformRoles.map((entry) => {
            entries.push({
              id: entry.platformId,
              label: entry.platformName,
              role: userGroups[entry.name],
            });
          });
        }

        setPlatforms(entries);
      },
      (e) => console.error('UserRepository.platformRoles', e),
      UserRepository.platformRoles(currentUser.id)
    );

    clientWrapper(
      (response) => {
        const entries: PopoverPlatformType[] = [];

        if (response.invites.length) {
          response.invites.map((entry) => {
            entries.push({
              id: entry.platformId,
              label: entry.platformName,
              role: userGroups[entry.platformRole],
              isInvite: true,
            });
          });
        }

        setInvites(entries);
      },
      (e) => console.error('UserRepository.getInvites', e),
      UserRepository.getInvites(currentUser?.id)
    );
  }, [currentUser?.id]);

  useEffect(() => {
    fetchData();
  }, [currentUser, showPopoverPlatform, isLoading]);

  return (
    <Wrapper>
      <Label>Platform</Label>
      <Action>
        <ActionLabel onClick={() => setShowPopoverPlatform(!showPopoverPlatform)}>
          <PlatformName>{currentPlatform?.name}</PlatformName>
          {invites.length > 0 && <Invites>{invites.length}</Invites>}
        </ActionLabel>
        <Arrow>
          {showPopoverPlatform ? (
            <Icon.ChevronUp onClick={() => setShowPopoverPlatform(!showPopoverPlatform)} />
          ) : (
            <Icon.ChevronDown onClick={() => setShowPopoverPlatform(!showPopoverPlatform)} />
          )}
        </Arrow>

        <StyledPopoverPlatform
          platforms={[...invites, ...platforms]}
          active={currentUser?.defaultPlatformId}
          show={showPopoverPlatform}
          onActiveChange={activeChange}
          onAcceptClick={acceptInvite}
          onManageClick={manageClick}
          onClose={() => setShowPopoverPlatform(false)}
        />
      </Action>
    </Wrapper>
  );
};
